// For creating synth tones with Tone.js

import * as Tone from 'tone';
import { createLogger } from '@airhead-games/shared/utilities';
const logger = createLogger('synthService');

export class SynthService {
  private _synth: Tone.Synth | null = null;
  private _synthInitialized = false;
  private _volumeNode: Tone.Volume | null = null;
  private _eq: Tone.EQ3 | null = null;
  private _toneOutput: GainNode | null = null;

  constructor(private _gainNode: GainNode | null) {
    // Defer initialization until resume() is called on WebAudioService
  }

  public initialize(): void {
    if (this._synthInitialized) {
      return;
    }
    try {
      // Create the synth but DON'T connect to destination
      this._synth = new Tone.Synth({
        oscillator: {
          type: 'square8',
        },
        envelope: {
          attack: 0.001,
          decay: 0.1,
          sustain: 0,
          release: 0.05,
        },
      });

      // Apply the SFX volume (0.8) to match other sound effects
      this._volumeNode = new Tone.Volume(-1.94); // -1.94 dB ≈ 0.8 linear

      // Apply EQ for brightness
      this._eq = new Tone.EQ3(0, 0, 6);

      // Connect to our gain node (for muting) instead of directly to destination
      if (this._gainNode) {
        // Create a connection point between Tone.js and Web Audio API
        this._toneOutput = Tone.getContext().createGain();

        // Chain everything together
        this._synth.chain(this._volumeNode, this._eq, this._toneOutput);

        // Connect to our gain node
        this._toneOutput.connect(this._gainNode);
      } else {
        // Fallback if audio graph isn't available
        this._synth.chain(this._volumeNode, this._eq, Tone.Destination);
      }

      this._synthInitialized = true;
      logger.debug('SynthService initialized successfully');
    } catch (error) {
      logger.error('Failed to initialize SynthService:', error);
    }
  }

  playNote(semitones: number = 0): void {
    if (!this._synth || !this._synthInitialized) {
      logger.warn('Synthesizer not initialized');
      return;
    }

    try {
      const baseNote = 'C5';
      const note = Tone.Frequency(baseNote).transpose(semitones).toFrequency();

      logger.info(
        `Playing xylophone-like note: ${semitones} semitones from ${baseNote} (frequency: ${note}Hz)`
      );

      // Very short duration for a pluck
      this._synth.triggerAttackRelease(note, 0.1);
    } catch (error) {
      logger.error('Failed to play synthetic note:', error);
    }
  }

  dispose(): void {
    if (this._synth) {
      this._synth.dispose();
      this._synth = null;
    }

    if (this._volumeNode) {
      this._volumeNode.dispose();
      this._volumeNode = null;
    }

    if (this._eq) {
      this._eq.dispose();
      this._eq = null;
    }

    if (this._toneOutput) {
      this._toneOutput.disconnect();
      this._toneOutput = null;
    }

    this._synthInitialized = false;
  }
}
